import { CommonModule } from '@angular/common'; // Import CommonModule
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompletionModel } from '@app-libs/model';
import { Subject, takeUntil } from 'rxjs';
import { CompletionProvidersApiService } from '../../../services/api/completion-providers-api-service';
import { ChatStateService } from '../../../services/chat/chat-state.service';
import { ConfigurationClientService } from '../../../services/client/configuration-client.service';
import { EventBusService, EventTypes } from '../../../services/client/event-bus.service';

@Component({
  selector: 'app-model-selector',
  imports: [MatIconModule, MatMenuModule, MatTooltipModule, CommonModule],
  templateUrl: './model-selector.component.html',
  styleUrls: ['./model-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelSelectorComponent implements OnInit, OnDestroy {
  selectedModel: CompletionModel | null = null;
  available_models: CompletionModel[] = [];
  default_model = '';
  menuOpen = false;

  // Observable for component lifecycle management
  private destroy$: Subject<void> = new Subject<void>();
  @Output() modelSelected = new EventEmitter<string>();

  constructor(
    private config: ConfigurationClientService,
    private chatState: ChatStateService,
    private eventBus: EventBusService,
    private providersService: CompletionProvidersApiService,
    private cdr: ChangeDetectorRef,
  ) {
    this.default_model = config.defaultModel;
  }

  async initModels(): Promise<void> {
    this.providersService.getModels().subscribe({
      next: (models) => {
        this.available_models = (models || []).sort((a, b) => a.title.localeCompare(b.title));

        // Determine which model to select based on priority:
        // 1. Current selectedModel if it exists
        // 2. PreferredModel if it exists
        // 3. Default model
        let modelToSelect = this.chatState.selectedModel;

        if (!modelToSelect && this.chatState.preferredModel) {
          modelToSelect = this.chatState.preferredModel;
        }

        if (!modelToSelect) {
          modelToSelect = this.default_model;
        }

        this.selectedModel = this.findCompletionModel(modelToSelect);

        // If no model found and we have models available, select the first one
        if (!this.selectedModel && this.available_models.length > 0) {
          this.selectedModel = this.available_models[0];
          // Update the state with the selected model
          this.selectModel(this.selectedModel);
        }

        // Trigger change detection after models are loaded
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading models:', error);
        this.available_models = [];
        this.cdr.markForCheck();
      },
    });
  }

  ngOnInit() {
    // Initialize models
    this.initModels();

    // Subscribe to selected model changes from state
    this.chatState.selectedModel$.pipe(takeUntil(this.destroy$)).subscribe((model) => {
      if (model && this.available_models.length > 0) {
        this.selectedModel = this.findCompletionModel(model);
        this.cdr.markForCheck();
      }
    });

    // Subscribe to preferred model changes from state
    this.chatState.preferredModel$.pipe(takeUntil(this.destroy$)).subscribe((preferredModel) => {
      if (preferredModel && this.available_models.length > 0) {
        // Only update if we don't have a selected model or if the selected model is the default
        if (!this.chatState.selectedModel || this.chatState.selectedModel === this.default_model) {
          this.selectedModel = this.findCompletionModel(preferredModel);
          // Update the state with the preferred model
          if (this.selectedModel) {
            this.selectModel(this.selectedModel);
          }
        }
        this.cdr.markForCheck();
      }
    });

    // Global events
    this.subscribeToEvent(
      EventTypes.UPDATE_SELECTED_MODEL,
      this.onUpdateSelectedModel.bind(this),
      this.destroy$,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper method to get appropriate icon for each model
  getModelIcon(model: CompletionModel): string {
    // Return the model's icon if it exists, otherwise use defaults based on provider
    // if (model.icon) {
    //   return model.icon;
    // }

    const modelName = model.name.toLowerCase();
    if (modelName.includes('gpt') || modelName.includes('o3-') || modelName.includes('o4-') || modelName.includes('o5-')) {
      return 'auto_awesome';
      // } else if (modelName.includes('gpt-3')) {
      //   return 'psychology';
    } else if (modelName.includes('claude')) {
      return 'face';
    } else if (modelName.includes('gemini')) {
      return 'star';
    } else if (modelName.includes('llama')) {
      return 'pets';
    }
    return 'smart_toy';
  }

  // Helper method to format model capabilities
  getModelCapabilities(model: CompletionModel): string {
    // Since CompletionModel doesn't have capabilities property, 
    // we can show other relevant info like max tokens
    // if (model.max_tokens || false) {
    //   return `Max ${model.max_tokens.toLocaleString()} tokens`;
    // }
    if (model.price_per_1k_tokens || false) {
      const our_price = model.price_per_1k_tokens * 1.5; // Adjusting price for our pricing model
      return `$${our_price.toLocaleString()} per 1m`;
    }
    return '';
  }

  onUpdateSelectedModel(event: any): void {
    console.debug('onUpdateSelectedModel:', event);
    this.selectedModel = this.findCompletionModel(event);
    this.cdr.markForCheck();
  }

  selectModel(model: CompletionModel): void {
    this.selectedModel = model;
    this.modelSelected.emit(this.selectedModel.name);
    console.log('Selected Model:', this.selectedModel.name);
    this.eventBus.emit({
      type: EventTypes.UPDATE_SELECTED_MODEL,
      data: this.selectedModel.name,
    });
  }

  findCompletionModel(modelId: string): any {
    return this.available_models.find((model) => model.name === modelId);
  }

  private subscribeToEvent(
    eventType: EventTypes,
    handler: (data: any) => void,
    destroy$: Subject<void>,
  ): void {
    this.eventBus
      .on(eventType)
      .pipe(takeUntil(destroy$))
      .subscribe((event) => handler(event.data));
  }
}
